import { MutationCache, QueryClient } from '@tanstack/react-query';

import { ApiError } from '@/api/errors';
import { notifyApiError } from '@/lib/notifyApiError';
import { notifyApiSuccess } from '@/lib/notifyApiSuccess';
import { isSuccessToastSuppressed } from '@/features/patient-record/lib/toastSuppression';

/**
 * App-wide React Query configuration.
 *
 * Defaults (rationale):
 *   - `staleTime: 30s` — most reads are patient data; we want fast
 *     navigation without hammering the backend, but not so stale that
 *     clinicians see yesterday's data.
 *   - `gcTime: 5m` — keep unmounted queries around long enough to survive
 *     route transitions.
 *   - `refetchOnWindowFocus: false` — avoid surprise refetches while a
 *     clinician is mid-chart; mutations will explicitly invalidate.
 *   - `retry` — bail immediately on 4xx (no point retrying a 403). Retry
 *     5xx / network up to 2 times with exponential backoff.
 */

const MAX_RETRIES = 2;

/**
 * Shared query-retry predicate (the app-wide default). Exported so feature
 * queries that override other options (e.g. `useTenant`'s `refetchInterval`)
 * can opt back into the SAME retry policy instead of re-deriving it — bail on
 * any 4xx (incl. 404), retry 5xx/network up to {@link MAX_RETRIES} times.
 */
export function shouldRetry(failureCount: number, error: unknown): boolean {
  if (failureCount >= MAX_RETRIES) return false;
  if (error instanceof ApiError) {
    if (error.status === 0) return true; // network / timeout
    if (error.status >= 500 && error.status < 600) return true;
    return false;
  }
  return true;
}

/**
 * Surface a toast for any failed mutation, and a green toast on success
 * when the hook opts in via `meta: { successMessage: '<i18n key>' }`.
 * Centralising both on the cache means every `useMutation` caller gets the
 * same UX without re-implementing it. Queries are intentionally left out —
 * pages typically render `FetchErrorCallout` for failed reads, and a toast
 * on top would double-notify.
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    mutationCache: new MutationCache({
      onError: (error) => {
        notifyApiError(error);
      },
      onSuccess: (_data, _vars, _ctx, mutation) => {
        // PTV2-1327: autosave-driven saves suppress the toast (the "Saved" status indicator conveys
        // it instead); an explicit Save still confirms.
        if (isSuccessToastSuppressed()) return;
        const key = mutation.meta?.successMessage;
        if (typeof key === 'string' && key.length > 0) notifyApiSuccess(key);
      },
    }),
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: false,
        refetchOnReconnect: 'always',
        retry: shouldRetry,
      },
      mutations: {
        retry: false,
      },
    },
  });
}

/**
 * Module-scoped accessor so non-React callers (the 401 handler invoked from
 * `src/api/http.ts`) can clear the cache without prop-drilling. main.tsx sets
 * this immediately after constructing the QueryClient.
 */
let activeQueryClient: QueryClient | null = null;

export function setActiveQueryClient(client: QueryClient | null): void {
  activeQueryClient = client;
}

export function getActiveQueryClient(): QueryClient | null {
  return activeQueryClient;
}
